import { Dispatch, useEffect, useRef, useState } from "react";
import styles from "./CartFormCards.module.css";
import { Action } from "../../../Bundles/Types";
import { BadgeType, CartConfigInterface, initialCartConfig } from "../../CartTypes";
import { DropZone, FormLayout, Icon, RangeSlider } from "@shopify/polaris";
import { DeleteIcon } from "@shopify/polaris-icons";
import { postData } from "../../../../utilities/networkCalls";
import config from "../../../../config";
import { truncateFileName } from "../../../../utilities/functions";
import Spinner from "../../../../components/Icons/Spinner";

const MAX_BADGE_UPLOAD_LIMIT = 20;
const MAX_FILE_SIZE = 1 * 1024 * 1024; // .5Mb
const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/svg", "image/svg+xml"];
const TrustBadgesForm = ({
    fields,
    dispatchCartConfig,
}: {
    submit: any;
    fields: any;
    showCartPreview: boolean;
    dispatchCartConfig: Dispatch<Action<CartConfigInterface>>;
}) => {
    const {
        trust_badges: { badges, badge_size },
    } = fields;
    const [uploading, setUploading] = useState(false);
    const [, setFiles] = useState<BadgeType[]>([]);

    const initialSet = useRef(true);

    useEffect(() => {
        if (initialSet.current && badges?.value?.length) {
            setFiles([...badges.value]);
            initialSet.current = false;
        } else if (initialSet.current) {
            setFiles([...initialCartConfig.trust_badges.badges]);
            initialSet.current = false;
        }
    }, [badges]);

    const handleBadgeSelection = (status: boolean, index: number) => {
        setFiles((ps) => {
            const arr = [...ps];
            arr[index] = { ...arr[index], selected: status };
            dispatchCartConfig({
                payload: {
                    badges: arr,
                },
                isComplex: true,
                key: "trust_badges",
            });
            badges.onChange(arr);
            return arr;
        });
    };

    const handleDeleteBadge = (index: number) => {
        setFiles((ps) => {
            let arr = [...ps];
            arr = arr.filter((_, idx) => idx != index);
            dispatchCartConfig({
                payload: {
                    badges: arr,
                },
                isComplex: true,
                key: "trust_badges",
            });
            badges.onChange(arr);
            return arr;
        });
    };

    const handleUpdateBadges = (obj: any) => {
        setFiles((ps: BadgeType[]) => {
            const arr: BadgeType[] = [...ps];
            arr.push(obj);

            dispatchCartConfig({
                payload: {
                    badges: arr,
                },
                isComplex: true,
                key: "trust_badges",
            });
            badges.onChange(arr);
            return arr;
        });
    };
    const handleDropZoneDrop = async (_dropFiles: File[], newFiles: File[]) => {
        const remainingFileCount =
            MAX_BADGE_UPLOAD_LIMIT - (badges?.value.filter((badge: BadgeType) => badge?.deletable)?.length ?? 0);
        if (newFiles.length > remainingFileCount) {
            shopify.toast.show(`You can only upload up to ${MAX_BADGE_UPLOAD_LIMIT} files.`, { isError: true });
            return [];
        }

        const validFiles: File[] = [];
        for (const file of newFiles) {
            if (file?.name) {
                const fileName = truncateFileName(file.name, 20);
                if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
                    shopify.toast.show(`${fileName} is not a supported file type.`, {
                        isError: true,
                    });
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                } else if (file.size > MAX_FILE_SIZE) {
                    shopify.toast.show(`${fileName} exceeds the ${MAX_FILE_SIZE}MB file size limit.`, {
                        isError: true,
                    });
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                } else {
                    validFiles.push(file);
                }
            }
        }

        const filesToProcess = validFiles.slice(0, remainingFileCount);

        for (const file of filesToProcess) {
            // Add a placeholder to show "Uploading..."
            const placeholderItem: BadgeType = {
                originalSrc: "",
                alt: file.name || "icon",
                deletable: true,
                selected: false,
            };

            setUploading(true);

            const data = new FormData();
            data.append("file", file);

            try {
                const response = await postData({
                    url: `/${config.appName}/upload`,
                    payload: data,
                });

                if (!response || !response.data || !response.data.url) {
                    throw new Error("Invalid response format");
                }

                handleUpdateBadges({ ...placeholderItem, originalSrc: response.data.url });
                setUploading(false);
            } catch (error) {
                setUploading(false);
                console.error("Error during file upload:", error);
                shopify.toast.show("Upload failed! Please try again.", {
                    isError: true,
                });
            }
        }
    };

    return (
        <>
            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Select Badges</div>
                <FormLayout>
                    {badges?.value?.length ? (
                        <div className={styles.badgesWrapper}>
                            {badges?.value?.map((badge: BadgeType, idx: number) => {
                                return (
                                    <div
                                        key={badge?.originalSrc + idx}
                                        className={`${styles.badgeIcon} ${badge?.selected && styles.selectedBadgeIcon}`}
                                        onClick={(e) => {
                                            e?.preventDefault();
                                            e?.stopPropagation();
                                            handleBadgeSelection(!badge?.selected, idx);
                                        }}
                                    >
                                        {badge?.deletable && (
                                            <span
                                                className={styles.deleteBadge}
                                                onClick={(e) => {
                                                    e?.preventDefault();
                                                    e?.stopPropagation();
                                                    handleDeleteBadge(idx);
                                                }}
                                            >
                                                <Icon source={DeleteIcon} tone={"critical"} />
                                            </span>
                                        )}
                                        <img
                                            src={badge?.originalSrc}
                                            alt={badge?.alt}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                                objectPosition: "center",
                                                aspectRatio: 1,
                                            }}
                                        />
                                    </div>
                                );
                            })}
                            {uploading && (
                                <div
                                    className={`${styles.badgeIcon}`}
                                    style={{ border: "1px solid #cfcfcf", cursor: "default" }}
                                >
                                    <Spinner size={30} color={"#6b6b6b"} />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.noBadgesAvailable}>No Icons Available</div>
                    )}
                    <div className={styles.uploadBadgeWrapper}>
                        <div className={styles.cardSubLabel} style={{ marginBottom: 10, marginTop: 10 }}>
                            Upload Badge
                        </div>
                        <DropZone onDrop={handleDropZoneDrop}>
                            <DropZone.FileUpload actionTitle="Click to upload or drag and drop" actionHint={""} />
                        </DropZone>
                    </div>

                    <RangeSlider
                        min={20}
                        max={100}
                        label="Badge Size"
                        value={badge_size.value}
                        onChange={(val) => {
                            badge_size.onChange(val?.toString());
                            dispatchCartConfig({
                                payload: {
                                    badge_size: val?.toString(),
                                },
                                isComplex: true,
                                key: "trust_badges",
                            });
                        }}
                        output
                        suffix={(badge_size.value || 0) + "px"}
                        error={badge_size.error}
                    />
                </FormLayout>
            </div>
        </>
    );
};

export default TrustBadgesForm;
